/**
 * SharePage — landing для Web Share Target API (Android) и Apple Shortcuts.
 *
 * URL: /messenger/share?text=...&att=1,2,3
 *
 * Пользователь приходит сюда после того как поделился чем-то из другого
 * приложения (TG, Safari, Photos, ...). Показываем список его чатов,
 * после клика — POST в /api/chat/topics/{tid}/messages с text + attachment_ids.
 * Затем navigate('/messenger?topic=N') чтобы попасть в чат с отправленным.
 */
import { useMemo, useState } from "react";
import { Navigate, useNavigate } from "@tanstack/react-router";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useAuth } from "@/auth/AuthContext";
import { fetchChatTopics, sendChatMessage } from "@/api/dashboard";
import type { ChatTopic } from "@/api/dashboard";
import { Avatar } from "@/components/prototype/Avatar";
import { Icon } from "@/components/prototype/Icon";
import { Spinner } from "@/components/Spinner";

export function SharePage() {
  const { user, isLoading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [query, setQuery] = useState("");

  const params = useMemo(() => {
    const p = new URLSearchParams(window.location.search);
    return {
      text: p.get("text") || "",
      attIds: (p.get("att") || "").split(",").map(Number).filter(Boolean),
    };
  }, []);

  const [editableText, setEditableText] = useState(params.text);

  const topicsQ = useQuery({
    queryKey: ["chat/topics"],
    queryFn: fetchChatTopics,
    enabled: !!user,
    staleTime: 30_000,
  });

  const sendMut = useMutation({
    mutationFn: (topicId: number) =>
      sendChatMessage(topicId, {
        text: editableText,
        reply_to_id: null,
        attachment_ids: params.attIds,
      }),
    onSuccess: (_, topicId) => {
      navigate({ to: "/messenger", search: { topic: String(topicId) } as never });
    },
    onError: (e: Error) => alert(`Не удалось отправить: ${e.message}`),
  });

  if (authLoading) return <Spinner size="md" label="Загрузка…"/>;
  if (!user) return <Navigate to="/login" replace />;

  const topics = (topicsQ.data || []).filter((t: ChatTopic) => {
    if (t.kind === "reports" || t.kind === "focal" || t.kind === "visits_schedule" || t.kind === "visits_funnel") return false;
    if (!query) return true;
    return (t.name || "").toLowerCase().includes(query.toLowerCase());
  });

  return (
    <div className="share-page">
      <header className="share-page-header">
        <button
          className="share-page-back"
          onClick={() => navigate({ to: "/messenger" })}
          aria-label="Отмена"
        >
          <Icon name="arrow-left" size={20}/>
        </button>
        <h1 className="share-page-title">Поделиться в чат</h1>
      </header>

      <div className="share-page-preview">
        <textarea
          className="share-page-text"
          value={editableText}
          onChange={(e) => setEditableText(e.target.value)}
          placeholder="Текст (можно отредактировать)"
          rows={3}
        />
        {params.attIds.length > 0 && (
          <div className="share-page-attachments">
            <Icon name="paperclip" size={14}/> Файлов: {params.attIds.length}
          </div>
        )}
      </div>

      <div className="share-page-search-wrap">
        <input
          type="search"
          className="share-page-search"
          placeholder="Поиск чата…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          autoFocus={false}
        />
      </div>

      <div className="share-page-list">
        {topicsQ.isLoading && <Spinner size="md" label="Чаты…"/>}
        {!topicsQ.isLoading && topics.length === 0 && (
          <div className="share-page-empty">Чаты не найдены</div>
        )}
        {topics.map((t: ChatTopic) => (
          <button
            key={t.id}
            type="button"
            className="share-page-topic"
            disabled={sendMut.isPending}
            onClick={() => sendMut.mutate(t.id)}
          >
            <Avatar
              name={t.name || "?"}
              size={40}
              color="#42a5f5"
            />
            <div className="share-page-topic-body">
              <div className="share-page-topic-name">{t.name || "(без имени)"}</div>
              {t.kind && (
                <div className="share-page-topic-kind">
                  {t.kind === "dm" ? "Личка" : t.kind === "group" ? "Группа" : t.kind === "private_group" ? "Закрытая группа" : t.kind}
                </div>
              )}
            </div>
            {sendMut.isPending && sendMut.variables === t.id && (
              <Spinner size="sm"/>
            )}
          </button>
        ))}
      </div>
    </div>
  );
}
